import { ReactNode, useLayoutEffect, useMemo, useRef } from 'react';

import { GuardedLocationContext } from 'app/router/route_guard/context';
import { useLocation, useNavigate } from 'react-router-dom';
import { PartnerTypes } from 'apis/PartnerApi';
import { useApp } from 'app';

type Guard = {

    // When set, the guard only triggers if the currently selected partner is one
    // of these types (e.g. block Distributors from a page that belongs to a
    // Testing Center). The user is then prompted to select a partner of
    // `partnerType`. When omitted, the guard triggers based on `partnerType`.
    blockedPartnerTypes?: number[];

    // When set, the route requires this specific partner type.
    // When omitted, the route requires any selected partner that is not the
    // SuperAdmin (system) partner — i.e. a real, non-sysadmin partner.
    partnerType?: number;
    path: string;

    // When set, the guard only applies if the URL query string contains this
    // key/value pair (e.g. OrderFor=Proxy). Lets a single path be guarded
    // differently depending on the query (proxy vs. self orders).
    query?: { key: string; value: string };

    // When false, SysAdmin users do NOT bypass this guard and must select a
    // partner just like everyone else. Defaults to true.
    sysAdminBypass?: boolean;
};

type RouteGuardProps = {
    children: ReactNode;
};

/**
 * Routes that require a partner to be selected before rendering.
 * - With `partnerType`, the route requires that specific partner type.
 * - Without `partnerType`, the route requires any non-SuperAdmin partner to be selected.
 * - With `sysAdminBypass: false`, SysAdmin users must also satisfy the guard.
 * When a user navigates to one of these routes without satisfying the guard,
 * the PartnerLookupModal is shown and the previous page stays visible.
 */
const GUARDS: Guard[] = [
    { partnerType: 4, path: '/students/import' },
    { path: '/orders/extend', sysAdminBypass: false },
    {
        partnerType: PartnerTypes.TestingCenter,
        path: '/orders/place',
        query: { key: 'OrderFor', value: 'Proxy' },
        sysAdminBypass: false
    },
    {
        blockedPartnerTypes: [PartnerTypes.Distributor],
        partnerType: PartnerTypes.TestingCenter,
        path: '/classrooms'
    },
    {
        blockedPartnerTypes: [PartnerTypes.Distributor],
        partnerType: PartnerTypes.TestingCenter,
        path: '/access-codes'
    }
];

const findGuard = (pathname: string, search: string): Guard | undefined => {

    const normalized = pathname.toLowerCase().replace(/\/$/, '');
    const params = new URLSearchParams(search);

    return GUARDS.find(guard => {

        const guardPath = guard.path.toLowerCase().replace(/\/$/, '');
        const pathMatches = normalized === guardPath || normalized.startsWith(`${guardPath}/`);

        if (!pathMatches) return false;

        // When a guard is scoped to a query param, it only applies if the param matches.
        return !guard.query || params.get(guard.query.key) === guard.query.value;

    });

};

/**
 * Intercepts route changes and checks if the target route requires a specific partner type.
 * If the partner type doesn't match, the PartnerLookupModal is shown and the previous page stays visible.
 *
 * Provides a guarded location via context — consumers (Routes) should use useGuardedLocation()
 * to render against the safe location rather than the blocked one.
 */
export const RouteGuard = (props: RouteGuardProps) => {

    const { children } = props;

    const { highestRole, launchPartnerLookupModal, selectedPartner } = useApp();
    const location = useLocation();
    const navigate = useNavigate();

    const previousLocationRef = useRef(location);
    const blockingRef = useRef(false);

    const currentPartnerType = useMemo(() => selectedPartner?.type ?? 0, [selectedPartner?.type]);

    // SysAdmins (highest role id 1) manage Access Codes and Classrooms across all partners from
    // system-level views, so they bypass the partner-type guard entirely.
    const isSysAdmin = highestRole?.id === 1;

    const guard = findGuard(location.pathname, location.search);

    // SysAdmins bypass guards that allow it (default), but not guards that explicitly opt out.
    const sysAdminBypass = guard?.sysAdminBypass ?? true;

    // A guard with blockedPartnerTypes only triggers for those current partner
    // types; a guard with a partnerType requires that exact type; a guard without
    // either requires any selected partner that is not the SuperAdmin partner.
    const isBlocked = !!guard && !(isSysAdmin && sysAdminBypass) && (
        guard.blockedPartnerTypes !== undefined
            ? guard.blockedPartnerTypes.includes(currentPartnerType)
            : guard.partnerType !== undefined
                ? currentPartnerType !== guard.partnerType
                : !selectedPartner || currentPartnerType === PartnerTypes.SuperAdmin
    );

    // When blocked, Routes renders against the previous location so the current page stays mounted
    const effectiveLocation = isBlocked ? previousLocationRef.current : location;

    useLayoutEffect(() => {

        if (blockingRef.current) return;

        if (!guard) {

            previousLocationRef.current = location;
            window.scrollTo(0, 0);
            return;

        }

        if (!isBlocked) {

            // Partner type matches — allow navigation
            previousLocationRef.current = location;
            window.scrollTo(0, 0);
            return;

        }

        // Block navigation — revert URL to previous location and show modal
        blockingRef.current = true;

        const previousPath = `${previousLocationRef.current.pathname}${previousLocationRef.current.search}`;
        navigate(previousPath, { replace: true });

        const destination = `${location.pathname}${location.search}`;

        (async () => {

            try {

                const partner = await launchPartnerLookupModal({
                    destination,
                    partnerType: guard.partnerType
                });

                if (!partner) blockingRef.current = false;

                // If partner was selected, the modal does window.location.href — no further action needed

            } catch {

                blockingRef.current = false;

            }

        })();

    }, [location.pathname, location.search, currentPartnerType, selectedPartner]);

    return (
        <GuardedLocationContext.Provider value={effectiveLocation}>
            {children}
        </GuardedLocationContext.Provider>
    );

};
